/**
 * RealUserInfoContext
 *
 * This module provides a React Context for managing and fetching real user information
 * from an external API. It integrates with Okta authentication to retrieve user data
 * based on authenticated user claims.
 *
 * Components:
 * - RealUserInfoProvider: Context provider component that fetches and manages user info
 * - useGetRealUserInfo: Custom hook to access and transform user info from the context
 *
 * Features:
 * - Fetches user information through `/proxy-api?operation=getUserInfo` once the user is authenticated
 * - Sends the user's Okta access token; the server resolves the user's identity from it
 * - Name-to-email fallback for the lookup is handled by the server
 * - Supports lock-aware fetching via the provided `lockAwareFetch` dependency
 * - Provides console-based error logging and optional callback transformation for consumers
 */

import { createContext, useContext, useState, useEffect } from 'react'
import { useOktaAuth } from '@okta/okta-react'

const UserInfoContext = createContext(null)

export default function RealUserInfoProvider({ children, lockAwareFetch }) {
  const [userInfo, setUserInfo] = useState({})

  const { authState, oktaAuth } = useOktaAuth()

  useEffect(() => {
    const fetchUserInfo = async () => {
      const {
        accessToken: { accessToken: token },
      } = await oktaAuth.tokenManager.getTokens()

      if (!token) {
        console.error('No access token available')
        return
      }

      lockAwareFetch(`${window.env?.VITE_IAR_API}/proxy-api?operation=getUserInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(response => {
          if (Array.isArray(response) && response[0]) {
            setUserInfo(response[0])
          }
        })
        .catch(error => {
          console.error('Error fetching real user info:', error)
        })
    }

    if (authState?.isAuthenticated) fetchUserInfo()
  }, [authState?.isAuthenticated])

  return <UserInfoContext.Provider value={userInfo}>{children}</UserInfoContext.Provider>
}

export function useGetRealUserInfo(callback = obj => obj) {
  const context = useContext(UserInfoContext)

  if (!context) {
    throw new Error('useGetRealUserInfo must be used within a RealUserInfoProvider')
  }

  return callback(context)
}
